import axios from 'axios';
import { Project, Component } from '../types';
import { getApiConfig } from '../config/api';
import { apiClient } from './apiClient';




// Get API configuration
const config = getApiConfig();

const api = axios.create({
  baseURL: config.BASE_URL,
  timeout: config.TIMEOUT,
  headers: {
    'x-api-key': config.API_KEY,
    'Content-Type': 'application/json',
  },
});

// Helper function to check if dev mode is enabled via URL parameter
const isDevMode = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('dev') === 'true';
  }
  return false;
};

// Add request interceptor for logging and authentication
api.interceptors.request.use(
  (config) => {
    // Only log API requests when dev parameter is present in URL
    if (isDevMode()) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    // Ensure API key is always present
    if (!config.headers['x-api-key']) {
      const apiKey = getApiConfig().API_KEY;
      config.headers['x-api-key'] = apiKey;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only log API errors when dev parameter is present in URL
    if (isDevMode()) {
      console.error('API Error:', error);
      
      // Handle specific error cases
      if (error.response) {
        switch (error.response.status) {
          case 401:
            console.error('Unauthorized: Invalid or missing API key');
            break;
          case 404:
            console.error('Not Found: Resource not found');
            break;
          case 500:
            console.error('Internal Server Error: Server-side issue');
            break;
          default:
            console.error(`HTTP Error: ${error.response.status}`);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Health check endpoint (no auth required)
export const healthApi = {
  check: async (): Promise<{status: string}> => {
    const response = await axios.get(`${config.BASE_URL}/health`);
    return response.data;
  },
};

export const projectsApi = {
  getAll: async (): Promise<Project[]> => {
    console.log('Projects API: getAll called');
    try {
      // Use new API client
      const apiProjects = await apiClient.getProjects();
      const projects = apiProjects;
      console.log('Projects API: projects data:', projects);
      
      return projects;
    } catch (error) {
      console.error('Projects API: error fetching projects:', error);
      throw error;
    }
  },
  
  getById: async (projectId: string): Promise<Project> => {
    console.log(`Projects API: getById called for ${projectId}`);
    try {
      const endpoint = `/api/projects/${projectId}`;
      console.log(`Projects API: endpoint: ${endpoint}`);
      const response = await api.get(endpoint);
      console.log(`Projects API: response for ${projectId}:`, response);
      const project = response.data;
      console.log(`Projects API: project data for ${projectId}:`, project);
      
      return project;
    } catch (error) {
      console.error(`Projects API: error fetching project ${projectId}:`, error);
      throw error;
    }
  },
  
  // Legacy method for backward compatibility - find project by name
  getByName: async (projectName: string): Promise<Project> => {
    console.log(`Projects API: getByName called for ${projectName}`);
    try {
      const projects = await projectsApi.getAll();
      const project = projects.find(p => p.name === projectName);
      
      if (!project) {
        throw new Error(`Project ${projectName} not found`);
      }
      
      return project;
    } catch (error) {
      console.error(`Projects API: error fetching project ${projectName}:`, error);
      throw error;
    }
  },
};

export const componentsApi = {
  getAll: async (): Promise<Component[]> => {
    console.log('Components API: getAll called');
    try {
      const response = await api.get('/api/components');
      console.log('Components API: response received:', response);
      const components = response.data;
      console.log('Components API: components data:', components);
      
      return components;
    } catch (error) {
      console.error('Components API: error fetching components:', error);
      throw error;
    }
  },
  
  getByProjectId: async (projectId: string): Promise<Component[]> => {
    console.log(`Components API: getByProjectId called for ${projectId}`);
    try {
      // Use new API client
      const apiComponents = await apiClient.getProjectComponents(projectId);
      const components = apiComponents;
      console.log(`Components API: components data for ${projectId}:`, components);
      
      return components;
    } catch (error) {
      console.error(`Components API: error fetching components for ${projectId}:`, error);
      throw error;
    }
  },
  
  // Legacy method for backward compatibility - get components by project name
  getByProject: async (projectName: string): Promise<Component[]> => {
    console.log(`Components API: getByProject called for ${projectName}`);
    try {
      // First get the project to find its ID
      const project = await projectsApi.getByName(projectName);
      return await componentsApi.getByProjectId(project.id);
    } catch (error) {
      console.error(`Components API: error fetching components for ${projectName}:`, error);
      throw error;
    }
  },
  
  getById: async (componentId: string): Promise<Component> => {
    // Search for component across all projects
    try {
      const components = await componentsApi.getAll();
      const component = components.find(c => c.id === componentId);
      
      if (!component) {
        throw new Error(`Component ${componentId} not found`);
      }
      
      return component;
    } catch (error) {
      console.error(`Failed to find component ${componentId}:`, error);
      throw error;
    }
  },
  
  // Legacy method for backward compatibility - find component by name
  getByName: async (componentName: string): Promise<Component> => {
    // Search for component across all projects
    try {
      const components = await componentsApi.getAll();
      const component = components.find(c => c.name === componentName);
      
      if (!component) {
        throw new Error(`Component ${componentName} not found in any project`);
      }
      
      return component;
    } catch (error) {
      console.error(`Failed to find component ${componentName}:`, error);
      throw error;
    }
  },
  
  // Search components across all projects
  search: async (query: string, filters?: {
    type?: string[];
    status?: string;
    tags?: string[];
    cms?: string[];
    themes?: string[];
  }): Promise<Component[]> => {
    try {
      const components = await componentsApi.getAll();
      
      // Filter components based on query and filters
      return components.filter(component => {
        // Text search
        const matchesQuery = !query || 
          component.name.toLowerCase().includes(query.toLowerCase()) ||
          (component.name && component.name.toLowerCase().includes(query.toLowerCase())) ||
          (component.description && component.description.toLowerCase().includes(query.toLowerCase()));
        
        if (!matchesQuery) return false;
        
        // Apply filters
        if (filters?.type && filters.type.length > 0) {
          if (!filters.type.includes(component.type || '')) return false;
        }
        
        if (filters?.status && filters.status !== component.status) {
          return false;
        }
        
        if (filters?.tags && filters.tags.length > 0) {
          if (!filters.tags.some(tag => (component.tags || []).includes(tag))) return false;
        }
        
        // CMS and themes filters commented out as they're not part of the API schema
        // if (filters?.cms && filters.cms.length > 0) {
        //   if (!filters.cms.some(cms => component.cms.includes(cms))) return false;
        // }
        
        // if (filters?.themes && filters.themes.length > 0) {
        //   if (!filters.themes.some(theme => component.themes.includes(theme))) return false;
        // }
        
        return true;
      });
    } catch (error) {
      console.error('Component search failed:', error);
      throw error;
    }
  },
};

// Utility function to check if API is accessible
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const health = await healthApi.check();
    return health.status === 'healthy';
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};

// Export the API instance for custom requests
export { api };


